import * as React from "react";
import { default as Form } from "@rjsf/core";
import { Field, ErrorSchema, FieldTemplateProps, ObjectFieldTemplateProps, RJSFValidationError, Widget, getUiOptions } from "@rjsf/utils";
import { createPrecompiledValidator } from "@rjsf/validator-ajv8";
import * as precompiledSchemas from "./schemas";
import { PrimaryButton, Stack, mergeStyles, getTheme, Label, format } from "@fluentui/react";
import { CheckboxWidget, PasswordWidget, RadioWidget, TextWidget, SelectWidget, EmailWidget } from "./Widgets";
import { ITestInputForm } from "../../models/ITestInputForm";
import { TitleField } from "./TitleField";
import { IServerValidationError } from "../../models/IServerValidationError";
import { Depths } from "@fluentui/react";
import { OAuthField } from "../../features/oauth/OAuthField";
import { HelpField } from "./HelpField";
import { RjsfUtils } from "./RjsfUtils";
import { ProofOfWorkWidget } from "../../features/proofOfWork/ProofOfWorkWidget";
import { DescriptionField } from "./DescriptionField";
import { ErrorListField } from "./ErrorListField";
import { Utils } from "../../Utils";
import { BasicAuthNoticeField } from "./BasicAuthNoticeField";
import { getLocalizedString } from "./LocalizedText";

/**
 * The props interface for the TestInputForm component
 */
export interface ITestInputFormProps {
  /**
   * Whether auto-fill is enabled
   */
  autoFillEnabled: boolean;

  /**
   * The template for the input form
   */
  formTemplate: ITestInputForm;

  /**
   * The form data
   */
  formData: Record<string, unknown>;

  /**
   * The form data
   */
  serverValidationErrors: IServerValidationError[];

  /**
   * The focused field ID
   */
  focusedFieldId: string;

  /**
   * When the form data changes
   */
  onChange: (formData?: Record<string, unknown>) => void;

  /**
   * When the form is submitted
   */
  onSubmit: (parameters?: Record<string, unknown>) => void;

  /**
   * When errors are encountered during client-side validation
   */
  onErrors: () => void;

  /**
   * Sets the focused field ID
   */
  setFocusedFieldId: (id: string) => void;
}

/**
 * The widgets map
 */
const widgets: { [name: string]: Widget } = {
  TextWidget: TextWidget,
  EmailWidget: EmailWidget,
  CheckboxWidget: CheckboxWidget,
  PasswordWidget: PasswordWidget,
  RadioWidget: RadioWidget,
  SelectWidget: SelectWidget,
};

/**
 * The fields map
 */
const fields: { [name: string]: Field } = {
  OAuthField: OAuthField,
  ProofOfWorkField: ProofOfWorkWidget,
  BasicAuthNoticeField: BasicAuthNoticeField,
};

/**
 * The ObjectFieldTemplate which renders a fieldset
 *
 * @param props - the props for the object field template.
 * @returns - the fieldset element.
 */
const ObjectFieldTemplate: React.FC<ObjectFieldTemplateProps> = (props) => {
  const { help } = RjsfUtils.getStringOptions(props.uiSchema);

  const titleId = props.title ? `${props.idSchema.$id}__title` : "";
  const descriptionId = props.description ? `${props.idSchema.$id}__description` : "";
  const helpId = help ? `${props.idSchema.$id}__help` : "";

  return (
    <fieldset id={props.idSchema.$id}>
      {props.title && <TitleField {...props} id={titleId} title={props.title} required={props.required} />}
      {props.description && <DescriptionField {...props} id={descriptionId} description={props.description} />}
      {help && props.uiSchema && (
        <HelpField {...props} uiSchema={props.uiSchema} id={helpId} helpText={help} inputId={props.idSchema.$id} />
      )}
      {props.properties.map((prop) => prop.content)}
    </fieldset>
  );
};

/**
 * The custom field template
 *
 * @param props - the field props
 * @returns the markup template for each field (each form row)
 */
const fieldTemplate = (props: FieldTemplateProps): JSX.Element => {
  const { id, label, children, rawErrors, rawHelp, description, hidden, required, classNames, uiSchema, schema } = props;
  if (hidden) {
    return <div className="hidden">{children}</div>;
  }

  // Whether the display the description depends on if rjsf thinks we should and we have a description
  const displayDescription = props.displayLabel && description;

  // We only want to display the label if rjsf thinks we should and we have a user-provided title
  // and if the rendered widget won't be a checkbox (since the title is displayed beside the checkbox)
  const isCheckboxWidget = uiSchema && uiSchema["ui:widget"] ? uiSchema["ui:widget"] === "checkbox" : schema.type === "boolean";
  const displayLabel = props.displayLabel && schema.title && !isCheckboxWidget;

  const showErrorList = getUiOptions(uiSchema)?.showErrorList !== false;
  return (
    <div className={classNames}>
      {displayLabel && (
        <Label required={required} id={`${id}__label`} htmlFor={id}>
          {getLocalizedString(label)}
        </Label>
      )}
      {displayDescription && description}
      {children}
      {showErrorList && rawErrors?.length ? <ErrorListField id={`${id}__error`} rawErrors={rawErrors} /> : undefined}
      {/* For object schemas, we render the help inside the ObjectFieldTemplate */}
      {props.schema.type !== "object" && props.uiSchema && (
        <HelpField {...props} id={`${id}__help`} helpText={rawHelp} inputId={id} uiSchema={props.uiSchema} />
      )}
    </div>
  );
};

/**
 * Interface for Type error parameters
 */
interface ITypeErrorParams {
  /**
   * The type of the error
   */
  type: string;
}

/**
 * Checks whether an object is compatible with ITypeErrorParams
 *
 * @param params - The object to check
 * @returns True if the object is compatible with ITypeErrorParams
 */
const isTypeErrorParams = (params?: unknown): params is ITypeErrorParams => {
  if (params && typeof params === "object") {
    return typeof (params as ITypeErrorParams).type === "string";
  }

  return false;
};

/**
 * Transforms errors into strings we like
 *
 * @param errors - The errors
 * @returns the transformed error
 */
const transformErrors = (errors: RJSFValidationError[]): RJSFValidationError[] => {
  return errors
    .map((error) => {
      switch (error.name) {
        case "required":
          error.message = WebsiteStrings.Validate_RequiredDefault;
          break;

        case "pattern":
          error.message = WebsiteStrings.Validate_RegexDefault;
          break;

        case "maxLength":
          error.message = WebsiteStrings.Validate_TooLong;
          break;

        // These errors are typically being triggered when we are using a dynamic schema
        // In these cases, errors are being generated even on the inactive parts of the schema
        // I don't really have a great way to detect these, but this seems to work for now.
        case "oneOf":
        case "enum":
          if (error.schemaPath && error.schemaPath.indexOf("/dependencies/") >= 0) {
            return null;
          }
          break;

        case "minLength":
          {
            const params = error.params as undefined | Record<string, unknown>;
            if (params && typeof params["limit"] === "number") {
              error.message = format(WebsiteStrings.Validate_TooShort, params["limit"]);
            }
          }
          break;

        case "type":
          if (isTypeErrorParams(error.params) && error.params.type === "number") {
            error.message = WebsiteStrings.Validate_IsNumber;
          }
          break;
      }

      if (error.property === ".notice.testAccountConsent") {
        error.message = WebsiteStrings.Default_cs_TestAccountConsentError;
      }

      return error;
    })
    .filter((error) => error !== null);
};

/**
 * The space between fields
 */
export const fieldSpacing = 15;

/**
 * Form for RCA Test input
 *
 * @param props - the test input form component props
 * @returns the markup
 */
export const TestInputForm: React.FC<ITestInputFormProps> = ({
  autoFillEnabled,
  serverValidationErrors,
  formTemplate,
  formData,
  focusedFieldId,
  onChange,
  onSubmit,
  onErrors,
  setFocusedFieldId,
}) => {
  const theme = getTheme();
  const formStyles = mergeStyles({
    padding: 20,
    backgroundColor: `var(--rca-card-background, ${theme.semanticColors.bodyBackground})`,
    border: "1px solid var(--rca-card-border, transparent)",
    boxShadow: `var(--rca-card-shadow, ${Depths.depth4})`,
    selectors: {
      fieldset: {
        borderStyle: "collapsed",
        borderWidth: 0,
        borderTopStyle: "solid",
        borderTopWidth: 1,
        borderTopColor: theme.palette.neutralLight,
        marginInline: 0,
        paddingInline: 0,
      },
      legend: {
        paddingInlineEnd: 6,
        paddingInlineStart: 0,
        marginInline: 0,
      },
      "fieldset#testInputForm": {
        borderTopWidth: 0,
        padding: 5,
      },
      "fieldset#testInputForm > legend": {
        ...theme.fonts.large,
        fontFamily: "inherit",
      },
      "fieldset#testInputForm .field": {
        marginTop: fieldSpacing,
        marginBottom: fieldSpacing,
      },
      "fieldset#testInputForm .field:last-child": {
        marginTop: 0,
        marginBottom: 0,
      },
      ".ms-TextField": {
        maxWidth: 500,
      },
      ".ms-Dropdown-container": {
        maxWidth: 500,
      },
      ".rca-Custom-field": {
        maxWidth: 500,
      },
      ".textareaLarge textarea": {
        width: "50%",
      },
      ".required-field-indicator": {
        color: theme.semanticColors.errorText,
        fontWeight: "bold",
      },
      ".testAccountConsent .field-description": {
        fontSize: theme.fonts.medium.fontSize,
        color: theme.semanticColors.bodyText,
      },
      "fieldset#testInputForm > .field-description": {
        fontSize: theme.fonts.medium.fontSize,
        color: theme.semanticColors.bodyText,
      },
      [Utils.mobileQuery]: {
        padding: 5,
      },
    },
  });

  const extraErrors: ErrorSchema<Record<string, unknown>> = {};
  for (const validationError of serverValidationErrors) {
    extraErrors[validationError.fieldName] = { __errors: validationError.errors };
  }

  const autoCompleteState = autoFillEnabled ? "on" : "off";

  const validator = createPrecompiledValidator(precompiledSchemas, formTemplate.schema);

  // This is a bit of a hack. It ensures that when the form is submitted,
  // the errors are re-rendered so they are announced by the screen reader
  const formRef = React.createRef<Form>();
  const [submitEnabled, setSubmitEnabled] = React.useState(true);
  const onErrorCallback = React.useCallback(() => {
    onErrors();
    const form = formRef.current;
    if (form) {
      setSubmitEnabled(false);
      const { errors, errorSchema } = form.state;
      form.setState({ ...form.state, errors: [], errorSchema: {} });
      setTimeout(() => {
        form.setState({ ...form.state, errors, errorSchema });
        setSubmitEnabled(true);
      }, 10);
    }
  }, []);

  return (
    <Form<Record<string, unknown>>
      className={formStyles}
      noHtml5Validate
      autoComplete={autoCompleteState}
      schema={formTemplate.schema}
      widgets={widgets}
      fields={fields}
      formData={formData}
      templates={{
        FieldTemplate: fieldTemplate,
        ObjectFieldTemplate: ObjectFieldTemplate,
        DescriptionFieldTemplate: DescriptionField,
        TitleFieldTemplate: TitleField,
      }}
      transformErrors={transformErrors}
      uiSchema={formTemplate.uiSchema}
      extraErrors={extraErrors}
      validator={validator}
      showErrorList={false}
      onChange={(e): void => onChange(e.formData)}
      onSubmit={(e): void => onSubmit(e.formData)}
      onError={onErrorCallback}
      onBlur={(id): void => {
        if (focusedFieldId === id) {
          setFocusedFieldId("");
        }
      }}
      onFocus={(id): void => {
        if (focusedFieldId !== id) {
          setFocusedFieldId(id);
        }
      }}
      ref={formRef}
    >
      <div style={{ marginTop: 20 }}>
        <Stack horizontal tokens={{ childrenGap: 15 }}>
          <PrimaryButton type="submit" iconProps={{ iconName: "Play" }} disabled={!submitEnabled}>
            {WebsiteStrings.Default_finishButtonText}
          </PrimaryButton>
        </Stack>
      </div>
    </Form>
  );
};
